/**
 * Resolver ของ API base path
 *
 * CAP ERP frontend ต้องเรียก API แบบ **same-origin** เท่านั้น (docs/SECURITY.md —
 * ไม่มี remote origin, ไม่มี production URL ใน source) ค่า config ที่ชี้ไป
 * origin อื่นถือว่าไม่ถูกต้องและถูก reject กลับเป็น same-origin เสมอ
 */

/** ค่าเริ่มต้น = same-origin root (เช่น `/v1/me/permissions`) */
export const DEFAULT_API_BASE_URL = '';

function stripTrailingSlashes(value: string): string {
  return value.replace(/\/+$/, '');
}

/**
 * แปลงค่า config ให้เป็น base path ที่ปลอดภัย
 *
 * - `undefined` / ว่าง → same-origin root
 * - path ที่ขึ้นต้นด้วย `/` → ใช้ตามนั้น (ตัด trailing slash)
 * - absolute URL ที่ origin เดียวกับหน้าเว็บ → ใช้เฉพาะ pathname
 * - อย่างอื่น (cross-origin, protocol-relative, relative path) → ปฏิเสธและกลับไป same-origin
 */
export function resolveApiBaseUrl(raw: string | undefined | null, currentOrigin: string): string {
  const candidate = (raw ?? '').trim();
  if (candidate === '') {
    return DEFAULT_API_BASE_URL;
  }

  // protocol-relative (`//host/...`) เป็น cross-origin ที่ปลอมเป็น path
  if (candidate.startsWith('//')) {
    return DEFAULT_API_BASE_URL;
  }

  if (candidate.startsWith('/')) {
    return stripTrailingSlashes(candidate);
  }

  let parsed: URL;
  try {
    parsed = new URL(candidate);
  } catch {
    return DEFAULT_API_BASE_URL;
  }

  if (parsed.origin !== currentOrigin) {
    return DEFAULT_API_BASE_URL;
  }

  return stripTrailingSlashes(parsed.pathname);
}

/** ต่อ base path เข้ากับ endpoint path (`path` ต้องขึ้นต้นด้วย `/`) */
export function buildApiUrl(baseUrl: string, path: string): string {
  return `${stripTrailingSlashes(baseUrl)}${path}`;
}

/** อ่านค่า `VITE_API_BASE_URL` แล้ว resolve ตามกติกา same-origin ด้านบน */
export function getConfiguredApiBaseUrl(): string {
  const origin = typeof window === 'undefined' ? '' : window.location.origin;
  return resolveApiBaseUrl(import.meta.env.VITE_API_BASE_URL, origin);
}
